import { deletePushSubscription, sendPushSubscription } from 'src/api/push.js'

/* global process */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replaceAll('-', '+')
    .replaceAll('_', '/')

  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((char) => char.codePointAt(0)))
}

export function isSWSupported() {
  return Boolean(navigator.serviceWorker)
}

function isPushSupported() {
  return 'PushManager' in window
}

function getOrCreateServiceWorker() {
  if (!isSWSupported()) return Promise.resolve()
  const swType = process.env.HAS_MODULE_SERVICE_WORKER ? 'module' : 'classic'
  return navigator.serviceWorker
    .register('/sw-pleroma.js', { type: swType })
    .catch((err) =>
      console.error('Unable to get or create a service worker.', err),
    )
}

function subscribePush(registration, isEnabled, vapidPublicKey) {
  if (!isEnabled) throw new Error('Web Push is disabled in config')
  if (!vapidPublicKey) throw new Error('VAPID public key is not found')

  const subscribeOptions = {
    userVisibleOnly: false,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  }
  return registration.pushManager.subscribe(subscribeOptions)
}

async function unsubscribePush(registration) {
  return registration.pushManager.getSubscription().then((subscription) => {
    if (subscription === null) {
      return 'No subscription'
    }
    return subscription.unsubscribe()
  })
}

// the kinds of notification the server should push, from the settings
const pushAlerts = (visibility) => ({
  follow: visibility.follows,
  favourite: visibility.likes,
  mention: visibility.mentions,
  reblog: visibility.repeats,
  move: visibility.moves,
})

export async function initServiceWorker(notificationsStore) {
  if (!isSWSupported()) return
  await getOrCreateServiceWorker()
  navigator.serviceWorker.addEventListener('message', (event) =>
    handleWorkerMessage(notificationsStore, event.data),
  )
}

// a message from the service worker; a clicked pop-up can stand for a
// group of notifications (ids)
export function handleWorkerMessage(notificationsStore, { type, id, ids }) {
  if (type !== 'notificationClicked') return
  if (ids?.length > 1) notificationsStore.groupClicked(ids)
  else notificationsStore.notificationClicked(id)
}

// a message to the active service worker, if there is one
const postToWorker = async (message) => {
  if (!isSWSupported()) return
  const { active: sw } =
    (await window.navigator.serviceWorker.getRegistration()) || {}
  if (!sw) return console.error('No serviceworker found!')
  sw.postMessage(message)
}

export const showDesktopNotification = (content) =>
  postToWorker({ type: 'desktopNotification', content })

// one notification's pop-up (its id: text or number), or all of them
export const closeDesktopNotification = ({ id }) =>
  postToWorker({
    type: 'desktopNotificationClose',
    content: id == null ? { all: true } : { id },
  })

export const updateFocus = () => postToWorker({ type: 'updateFocus' })

export function registerPushNotifications(
  isEnabled,
  vapidPublicKey,
  token,
  notificationVisibility,
) {
  if (isPushSupported()) {
    getOrCreateServiceWorker()
      .then((registration) =>
        subscribePush(registration, isEnabled, vapidPublicKey),
      )
      .then((subscription) =>
        sendPushSubscription({
          subscription,
          alerts: pushAlerts(notificationVisibility),
          credentials: token,
        }),
      )
      .catch((e) =>
        console.warn(`Failed to setup Web Push Notifications: ${e.message}`),
      )
  }
}

export function unregisterPushNotifications(token) {
  if (isPushSupported()) {
    return getOrCreateServiceWorker()
      .then((registration) => {
        return unsubscribePush(registration).then((result) => [
          registration,
          result,
        ])
      })
      .then(([, unsubResult]) => {
        if (unsubResult === 'No subscription') return
        if (!unsubResult) {
          console.warn("Push subscription cancellation wasn't successful")
        }
        return deletePushSubscription({ credentials: token })
      })
      .catch((e) => {
        console.warn(`Failed to disable Web Push Notifications: ${e.message}`)
      })
  }
}

export const shouldCache = process.env.NODE_ENV === 'production'
export const cacheKey = 'pleroma-fe'
export const emojiCacheKey = 'pleroma-fe-emoji'

export const clearCache = (key) => caches.delete(key)

export { getOrCreateServiceWorker }
